import React from 'react';
import { Link } from 'react-router-dom';
import ActivitySection from './ActivitySection';
import SectionState from './SectionState';
import { ShowMore, usePagedItems } from './usePagedItems';
import { dayMonth, paperScore, percent } from './lib/activityFormat';
import { quizReviewHref } from '../../../lib/dashboardParams';

/** A quiz row's badge: where it came from, or that only its results were kept. */
export function quizBadge(quiz) {
  if (quiz.source === 'app') return { text: 'pathbuddy.app', title: 'Made on pathbuddy.app: totals only' };
  if (!quiz.questions_saved) return { text: 'Older quiz', title: 'Made before questions were saved: topics and results only' };
  return null;
}

/** The title a row reads by: the student's topic focus, or a plain name without one. */
export const quizTitle = (quiz) => quiz.topic_focus || 'Custom quiz';

/**
 * The review link: a router <Link> to the review's full URL
 * (/teacher-dashboard?…&quiz=…), so cmd-click and "open in new tab" reach
 * it, as Exam practice's paper links do. A plain click opens it in place.
 */
const ReviewLink = ({ quiz, links }) => (
  <Link
    to={links.href(quiz.session_id)}
    aria-label={`Review ${quizTitle(quiz)}, ${dayMonth(quiz.at)}`}
    onClick={(e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();
      links.open(quiz.session_id);
    }}
  >
    {quizTitle(quiz)}
  </Link>
);

const QuizRow = ({ quiz, links }) => {
  const score = paperScore(quiz.correct, quiz.total, quiz.questions);
  const badge = quizBadge(quiz);
  return (
    <li className="td-act-rowline">
      <span className="td-act-when">{dayMonth(quiz.at)}</span>
      <div className="td-act-rowline__main">
        <div className="td-act-rowtitle">
          {links ? <ReviewLink quiz={quiz} links={links} /> : quizTitle(quiz)}
          {badge ? <span className="td-act-pill td-act-pill--b" title={badge.title}>{badge.text}</span> : null}
        </div>
        <div className="td-act-rowsub">{score.detail}</div>
      </div>
      <span className="td-act-mark">
        {score.figure === '—' ? score.figure : <>{score.figure} <small>correct</small></>}
      </span>
    </li>
  );
};

const tile = (label, value, caption) => (
  <div className="td-stat">
    <span className="td-stat__eyebrow">{label}</span>
    <span className="td-stat__value">{value}</span>
    <span className="td-stat__caption">{caption}</span>
  </div>
);

const Summary = ({ summary }) => (
  <div className="td-act-statrow td-act-statrow--3">
    {tile('Quizzes', summary.quizzes, 'generated and answered in this period')}
    {tile('Questions answered', summary.questions_answered, `${summary.correct} correct`)}
    {tile(
      'Accuracy',
      percent(summary.accuracy) || '—',
      summary.questions_answered ? 'of the questions answered' : 'No questions answered',
    )}
  </div>
);

/**
 * Quiz generator, from GET …/activity/quizzes: the custom quizzes the
 * student generated from their own files, with the period's totals and a
 * row per quiz, shaped like Exam practice's papers. With
 * `onOpenQuiz(sessionId)` (and `quizHref(sessionId)` for the links' href)
 * each row links to its review (QuizReview). Totals cover the whole period,
 * not just the rows loaded.
 */
const QuizSection = ({
  state, fetchPage, onOpenQuiz, quizHref,
}) => {
  const first = state.status === 'ready' ? state.data : null;
  const paged = usePagedItems(first, 'quizzes', fetchPage);
  const links = onOpenQuiz
    ? { open: onOpenQuiz, href: quizHref || ((id) => quizReviewHref(new URLSearchParams(), id)) }
    : null;

  return (
    <ActivitySection
      id="quizzes"
      title="Quiz generator"
      description="Quizzes the student generated from their own uploaded papers, and how they did."
    >
      <SectionState state={state} what="quizzes" emptyTitle="No generated quizzes in this period" />
      {first ? (
        <>
          <Summary summary={first.summary} />
          <div className="td-card">
            <h4 className="td-card__title">Quizzes</h4>
            <ul className="td-act-rows">
              {paged.items.map((quiz) => <QuizRow key={quiz.session_id} quiz={quiz} links={links} />)}
            </ul>
            <ShowMore paged={paged} what="quizzes" />
          </div>
        </>
      ) : null}
    </ActivitySection>
  );
};

export default QuizSection;
